import React, { useState, useEffect, useCallback } from "react";
import {
  FaSearch,
  FaSpinner,
  FaChevronLeft,
  FaChevronRight,
  FaExclamationTriangle,
} from "react-icons/fa";
import apiRequest from "../services/api";

const PAGE_SIZE_OPTIONS = [10, 20, 50];

export default function ExpiredBatch() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalElements, setTotalElements] = useState(0);

  const [search, setSearch] = useState("");
  const [input, setInput] = useState("");

  const getStatusBadge = (status, alternativeDate) => {
    switch (status) {
      case "EXPIRED":
        return "bg-red-50 text-red-700 border-red-200 dark:bg-red-500/10 dark:text-red-400 dark:border-red-500/20";
      case "NEAR_EXPIRY":
        return "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-500/10 dark:text-amber-400 dark:border-amber-500/20";
      case "SAFE":
        return "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-400 dark:border-emerald-500/20";
      default:
        return new Date(alternativeDate) <= new Date()
          ? "bg-red-50 text-red-700 border-red-200 dark:bg-red-500/10 dark:text-red-400 dark:border-red-500/20"
          : "bg-slate-100 text-slate-600 border-slate-200 dark:bg-slate-500/10 dark:text-slate-400 dark:border-white/5";
    }
  };

  const fetchBatchInventory = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        size: size.toString(),
      });
      if (search) params.append("productId", search);

      const res = await apiRequest(
        `stocks/batches/expired?${params.toString()}`
      );
      const payload = res?.data || res;
      setRows(payload?.content || []);
      setTotalPages(payload?.totalPages || 1);
      setTotalElements(payload?.totalElements || 0);
    } catch (err) {
      setError(err?.message || "Unable to load expiration tracking metrics.");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [page, size, search]);

  useEffect(() => {
    fetchBatchInventory();
  }, [fetchBatchInventory]);

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      setSearch(input.trim());
      setPage(0);
    }, 400);
    return () => clearTimeout(delayDebounce);
  }, [input]);

  return (
    <div
      className="min-h-full p-4 md:p-8 font-sans
      bg-slate-50 text-slate-800
      dark:bg-slate-950 dark:text-slate-100"
    >
      <div className="max-w-7xl mx-auto space-y-6">
        {/* HEADER */}
        <div
          className="flex flex-col md:flex-row md:items-center justify-between gap-4 p-5 rounded-2xl shadow-sm border backdrop-blur-md
          bg-white border-slate-200
          dark:bg-slate-900/40 dark:border-white/10 dark:shadow-xl"
        >
          <div className="flex items-center gap-3.5">
            <div
              className="p-3 rounded-xl shadow-inner border
              bg-gradient-to-br from-amber-100 to-orange-50 border-amber-200 text-amber-600
              dark:from-amber-500/20 dark:to-orange-500/10 dark:border-amber-500/30 dark:text-amber-400"
            >
              <FaExclamationTriangle className="text-2xl" />
            </div>
            <div>
              <h1 className="text-xl font-black tracking-tight uppercase text-slate-900 dark:text-white">
                Batch Expiration Control
              </h1>
              <p className="text-xs mt-0.5 text-slate-500 dark:text-slate-400">
                <span className="font-mono font-semibold text-amber-600 dark:text-amber-400">
                  {totalElements}
                </span>{" "}
                critical or near-expiry batches monitored
              </p>
            </div>
          </div>
        </div>

        {/* SEARCH */}
        <div
          className="grid grid-cols-1 gap-3 p-3 rounded-xl border backdrop-blur-sm
          bg-white border-slate-200
          dark:bg-slate-900/20 dark:border-white/5"
        >
          <div className="relative">
            <span className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-400 dark:text-slate-500">
              <FaSearch className="text-xs" />
            </span>
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Filter by explicit Product ID..."
              className="w-full rounded-xl pl-9 pr-4 py-2.5 text-xs transition focus:outline-none focus:border-amber-500/40
                bg-slate-50 border border-slate-200 text-slate-800 placeholder-slate-400
                dark:bg-slate-950/60 dark:border-white/10 dark:text-white dark:placeholder-slate-600"
            />
          </div>
        </div>

        {/* TABLE */}
        <div
          className="border rounded-2xl shadow-sm backdrop-blur-md
          bg-white border-slate-200
          dark:bg-slate-900/30 dark:border-white/10 dark:shadow-2xl"
        >
          <div className="overflow-x-auto rounded-2xl">
            <table className="w-full text-left border-collapse min-w-[1200px]">
              <thead>
                <tr
                  className="border-b text-[10px] font-bold tracking-wider uppercase
                  bg-slate-50 border-slate-200 text-slate-500
                  dark:bg-slate-950/50 dark:border-white/10 dark:text-slate-400"
                >
                  <th className="px-5 py-4">Batch Number</th>
                  <th className="px-4 py-4">Product / Code</th>
                  <th className="px-4 py-4 text-right">Received</th>
                  <th className="px-4 py-4 text-right">Sold</th>
                  <th className="px-4 py-4 text-right">Available</th>
                  <th className="px-4 py-4 text-right">Damaged</th>
                  <th className="px-4 py-4 text-right">Expired</th>
                  <th className="px-4 py-4 text-right">Purchase Cost</th>
                  <th className="px-4 py-4 text-right">Wholesale</th>
                  <th className="px-4 py-4 text-right">Selling</th>
                  <th className="px-4 py-4 text-center">Status</th>
                  <th className="px-5 py-4">Expiry Date</th>
                </tr>
              </thead>
              <tbody
                className="divide-y font-mono text-xs
                divide-slate-100 text-slate-600
                dark:divide-white/5 dark:text-slate-300"
              >
                {rows.length === 0 && !loading ? (
                  <tr>
                    <td
                      colSpan={12}
                      className="text-center py-12 text-slate-500 font-sans italic dark:text-slate-500"
                    >
                      No matching high-risk expiration batches cataloged.
                    </td>
                  </tr>
                ) : (
                  rows.map((b) => (
                    <tr
                      key={b.id || b.batchNumber}
                      className="group transition hover:bg-slate-50 dark:hover:bg-white/[0.02]"
                    >
                      <td className="px-5 py-3.5 font-sans font-bold tracking-wide text-slate-900 dark:text-white">
                        {b.batchNumber}
                      </td>
                      <td className="px-4 py-3.5 font-sans">
                        <div className="font-semibold text-slate-800 dark:text-slate-200">
                          {b.productName || "Tracked Batch Item"}
                        </div>
                        <div className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">
                          {b.barcode || b.productBarcode || "-"}
                        </div>
                      </td>
                      <td className="px-4 py-3.5 text-right">
                        {b.quantityReceived}
                      </td>
                      <td className="px-4 py-3.5 text-right">
                        {b.quantitySold}
                      </td>
                      <td className="px-4 py-3.5 text-right font-semibold text-emerald-600 dark:text-emerald-400">
                        {b.quantityAvailable || b.remainingQuantity}
                      </td>
                      <td className="px-4 py-3.5 text-right text-red-600 dark:text-red-400">
                        {b.quantityDamaged || 0}
                      </td>
                      <td className="px-4 py-3.5 text-right text-amber-600 dark:text-amber-500">
                        {b.quantityExpired || 0}
                      </td>
                      <td className="px-4 py-3.5 text-right">
                        {b.purchaseCost}
                      </td>
                      <td className="px-4 py-3.5 text-right">
                        {b.wholesalePrice}
                      </td>
                      <td className="px-4 py-3.5 text-right font-semibold text-slate-800 dark:text-slate-200">
                        {b.sellingPrice}
                      </td>
                      <td className="px-4 py-3.5 text-center font-sans">
                        <span
                          className={`px-2 py-0.5 rounded text-[10px] font-bold tracking-wider uppercase border ${getStatusBadge(
                            b.expiryStatus,
                            b.expiryDate
                          )}`}
                        >
                          {b.expiryStatus ||
                            (new Date(b.expiryDate) <= new Date()
                              ? "EXPIRED"
                              : "TRACKED")}
                        </span>
                      </td>
                      <td className="px-5 py-3.5 whitespace-nowrap font-sans font-medium text-slate-800 dark:text-slate-200">
                        {b.expiryDate || (
                          <span className="text-slate-400 dark:text-slate-500">
                            -
                          </span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {loading && (
            <div
              className="p-8 flex items-center justify-center gap-3 text-xs border-t
              bg-slate-50 text-slate-500 border-slate-200
              dark:bg-slate-950/20 dark:text-slate-400 dark:border-white/5"
            >
              <FaSpinner className="animate-spin text-amber-500 text-base" />
              <span>Compiling expiration parameters...</span>
            </div>
          )}

          {error && (
            <div className="p-6 text-center text-sm text-red-600 dark:text-red-400 border-t border-slate-200 dark:border-white/5">
              {error}
            </div>
          )}
        </div>

        {/* PAGINATION */}
        <div
          className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3 rounded-xl border
          bg-white border-slate-200
          dark:bg-slate-900/20 dark:border-white/5"
        >
          <div className="text-xs text-slate-500 dark:text-slate-400">
            Showing{" "}
            <span className="font-mono font-semibold text-amber-600 dark:text-amber-400">
              {rows.length}
            </span>{" "}
            of{" "}
            <span className="font-mono font-semibold text-slate-700 dark:text-slate-200">
              {totalElements}
            </span>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              disabled={page === 0}
              onClick={() => setPage((p) => p - 1)}
              className="p-2 rounded-lg border text-slate-600 disabled:opacity-40
                bg-slate-50 border-slate-200 hover:bg-slate-100
                dark:bg-white/5 dark:border-white/10 dark:text-slate-300 dark:hover:bg-white/10"
            >
              <FaChevronLeft className="text-xs" />
            </button>
            <span className="text-xs font-mono text-slate-600 dark:text-slate-300">
              Page {page + 1} / {totalPages}
            </span>
            <button
              type="button"
              disabled={page >= totalPages - 1}
              onClick={() => setPage((p) => p + 1)}
              className="p-2 rounded-lg border text-slate-600 disabled:opacity-40
                bg-slate-50 border-slate-200 hover:bg-slate-100
                dark:bg-white/5 dark:border-white/10 dark:text-slate-300 dark:hover:bg-white/10"
            >
              <FaChevronRight className="text-xs" />
            </button>
          </div>

          <select
            value={size}
            onChange={(e) => {
              setSize(Number(e.target.value));
              setPage(0);
            }}
            className="rounded-lg px-3 py-2 text-xs border font-semibold
              bg-slate-50 border-slate-200 text-slate-800
              dark:bg-slate-950/60 dark:border-white/10 dark:text-white"
          >
            {PAGE_SIZE_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {s} / page
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
}
